(() => {
  const money = (cents) => {
    if (typeof cents === "number" && typeof cents.toCurrency === "function") return cents.toCurrency();
    const currency = window.theme?.currency?.current || window.Shopify?.currency?.active || "USD";
    return (Number(cents || 0) / 100).toLocaleString("en-US", { style: "currency", currency });
  };

  const escapeHTML = (value = "") => String(value).replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  })[char]);

  const inventoryMaxByVariant = new Map();
  const inventoryWarningText = "Only available stock can be added for this product.";

  const toPositiveInt = (value) => {
    const number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : null;
  };

  const rememberInventoryMax = (variantId, max) => {
    const id = String(variantId || "").trim();
    const quantity = toPositiveInt(max);
    if (id && quantity) inventoryMaxByVariant.set(id, quantity);
    return quantity;
  };

  const getKnownInventoryMax = (variantId) => {
    const id = String(variantId || "").trim();
    return id && inventoryMaxByVariant.has(id) ? inventoryMaxByVariant.get(id) : null;
  };

  const showInventoryWarning = (target, message = inventoryWarningText) => {
    const scope = target?.closest?.("form, [data-amt-cart-drawer], .cart-wrapper") || target || document.body;
    if (!scope) return;
    let warning = scope.querySelector?.(".amt-inventory-warning");
    if (!warning) {
      warning = document.createElement("div");
      warning.className = "amt-inventory-warning";
      warning.setAttribute("role", "alert");
      const insertionPoint = scope.querySelector?.(".product-card_actions, .product-cta-row, .amt-cart-drawer__shipping, .cart-summary-box") || scope;
      insertionPoint.insertAdjacentElement?.("afterend", warning);
    }
    warning.textContent = message;
    window.setTimeout(() => warning?.classList.add("is-visible"), 10);
  };

  const hydrateInventoryMaxes = (root = document) => {
    root.querySelectorAll?.("[data-variant-id][data-inventory-max], form[action*='/cart/add'] [name='id'][data-inventory-max], form[action*='/cart/add'] option[data-inventory-max]").forEach((element) => {
      const variantId = element.dataset.variantId || element.value || element.getAttribute("value");
      const max = element.dataset.inventoryMax || element.getAttribute("data-inventory-max");
      rememberInventoryMax(variantId, max);
    });
  };

  const getFormVariantField = (form) => form?.querySelector?.("[name='id']:checked, select[name='id'], input[name='id']");

  const getFormVariantId = (form, formData) => {
    const field = getFormVariantField(form);
    return String(formData?.get("id") || field?.value || "").trim();
  };

  const getFormQuantity = (form, formData) => {
    const quantityField = form?.querySelector?.("[name='quantity']");
    return Math.max(1, parseInt(formData?.get("quantity") || quantityField?.value || 1, 10) || 1);
  };

  const isQuoteOnlyForm = (form) => {
    if (!form) return false;
    const quoteScope = form.closest?.("[data-amt-quote-only='true']");
    const explicitQuoteOnly = form.dataset.amtQuoteOnly === "true" || quoteScope?.dataset.amtQuoteOnly === "true";
    const priceValue = form.dataset.amtPrice ?? quoteScope?.dataset.amtPrice;
    const hasExplicitPrice = priceValue !== undefined && priceValue !== "";
    return explicitQuoteOnly || (hasExplicitPrice && Number(priceValue) === 0);
  };

  const getFormInventoryMax = (form, variantId) => {
    const field = getFormVariantField(form);
    const selectedOption = field?.tagName === "SELECT" ? field.options[field.selectedIndex] : null;
    const quantityField = form?.querySelector?.("[name='quantity']");
    return rememberInventoryMax(variantId,
      selectedOption?.dataset.inventoryMax ||
      field?.dataset.inventoryMax ||
      quantityField?.dataset.inventoryMax ||
      quantityField?.getAttribute("max") ||
      form?.dataset.inventoryMax ||
      getKnownInventoryMax(variantId)
    );
  };

  const updateProductFormVariantMax = (form) => {
    if (!form || !form.matches?.("form[action*='/cart/add']")) return;
    const formData = new FormData(form);
    const variantId = getFormVariantId(form, formData);
    const max = getFormInventoryMax(form, variantId);
    const quantityField = form.querySelector("[name='quantity']");
    if (!quantityField) return;
    if (max) {
      quantityField.setAttribute("max", String(max));
      quantityField.dataset.max = String(max);
      quantityField.dataset.inventoryMax = String(max);
      const quantityInput = quantityField.closest("quantity-input");
      if (quantityInput) {
        quantityInput.dataset.max = String(max);
        quantityInput.dataset.inventoryMax = String(max);
      }
      if ((parseInt(quantityField.value, 10) || 1) > max) quantityField.value = max;
    } else {
      quantityField.removeAttribute("max");
      delete quantityField.dataset.max;
      delete quantityField.dataset.inventoryMax;
    }
  };

  const getCartQuantityForVariant = (cart, variantId) => {
    const id = String(variantId || "");
    return (cart?.items || []).reduce((total, item) => {
      return String(item.variant_id) === id ? total + Number(item.quantity || 0) : total;
    }, 0);
  };

  const validateAddToCartInventory = (form, formData) => {
    if (isQuoteOnlyForm(form)) {
      showInventoryWarning(form, "Please request a quote for this product.");
      return Promise.resolve(false);
    }
    const variantId = getFormVariantId(form, formData);
    const requestedQuantity = getFormQuantity(form, formData);
    const max = getFormInventoryMax(form, variantId);
    if (!max) return Promise.resolve(true);
    if (requestedQuantity > max) {
      showInventoryWarning(form, `Only ${max} item${max === 1 ? "" : "s"} available for this product.`);
      return Promise.resolve(false);
    }
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        const currentQuantity = getCartQuantityForVariant(cart, variantId);
        if (currentQuantity + requestedQuantity > max) {
          const remaining = Math.max(0, max - currentQuantity);
          showInventoryWarning(form, remaining > 0
            ? `Only ${remaining} more item${remaining === 1 ? "" : "s"} can be added.`
            : `You already have the available ${max} item${max === 1 ? "" : "s"} in cart.`);
          return false;
        }
        return true;
      })
      .catch(() => {
        showInventoryWarning(form);
        return false;
      });
  };

  const syncAddToCartButtonStates = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    document.querySelectorAll("form[action*='/cart/add']").forEach((form) => {
      if (isQuoteOnlyForm(form)) return;
      const formData = new FormData(form);
      const variantId = getFormVariantId(form, formData);
      const max = getFormInventoryMax(form, variantId);
      if (!max) return;
      const isAtLimit = getCartQuantityForVariant(cart, variantId) >= max;
      const button = form.querySelector("button[name='add'], .js-atc-btn");
      if (button) {
        button.toggleAttribute("disabled", isAtLimit);
        button.setAttribute("aria-disabled", isAtLimit ? "true" : "false");
        button.classList.toggle("amt-stock-limit-reached", isAtLimit);
      }
    });
  };

  const drawer = {
    root: null,
    items: null,
    total: null,
    discount: null,
    discountValue: null,
    shipping: null,
    init() {
      this.root = document.querySelector("[data-amt-cart-drawer]");
      if (!this.root || this.root.dataset.ready === "true") return;
      this.root.dataset.ready = "true";
      this.items = this.root.querySelector("[data-amt-cart-items]");
      this.total = this.root.querySelector("[data-amt-cart-total]");
      this.discount = this.root.querySelector("[data-amt-cart-discount]");
      this.discountValue = this.root.querySelector("[data-amt-cart-discount-value]");
      this.shipping = this.root.querySelector("[data-amt-cart-shipping]");

      this.root.querySelectorAll("[data-amt-cart-close]").forEach((button) => {
        button.addEventListener("click", () => this.close());
      });

      this.root.querySelectorAll(".amt-cart-drawer__view").forEach((link) => {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
          window.location.href = link.getAttribute("href") || "/cart";
        }, true);
      });

      this.root.addEventListener("click", (event) => {
        const qtyButton = event.target.closest("[data-amt-cart-qty]");
        const removeButton = event.target.closest("[data-amt-cart-remove]");
        if (qtyButton) {
          const item = qtyButton.closest("[data-amt-cart-item]");
          const currentQuantity = Number(item?.dataset.quantity || 0);
          const delta = Number(qtyButton.dataset.amtCartQty || 0);
          const max = toPositiveInt(item?.dataset.inventoryMax);
          const quantity = Math.max(0, currentQuantity + delta);
          if (max && delta > 0 && quantity > max) {
            showInventoryWarning(this.root, `Only ${max} item${max === 1 ? "" : "s"} available for this product.`);
            return;
          }
          if (item) this.change(item.dataset.key, quantity);
        }
        if (removeButton) {
          const item = removeButton.closest("[data-amt-cart-item]");
          if (item) this.change(item.dataset.key, 0);
        }
      });
    },
    open() {
      this.init();
      if (!this.root) return;
      this.root.classList.add("is-open");
      this.root.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("overflow-hidden");
      this.refresh();
    },
    close() {
      if (!this.root) return;
      this.root.classList.remove("is-open");
      this.root.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("overflow-hidden");
    },
    refresh() {
      return fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then((response) => response.json())
        .then((cart) => {
          this.render(cart);
          document.dispatchEvent(new CustomEvent("cart-change", { detail: cart }));
          if (window.Cart && typeof Cart === "object") Object.assign(Cart, cart);
          return cart;
        });
    },
    change(key, quantity) {
      if (!key) return Promise.resolve();
      this.root?.classList.add("is-updating");
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity })
      })
        .then((response) => response.json())
        .then((cart) => {
          this.render(cart);
          document.dispatchEvent(new CustomEvent("cart-change", { detail: cart }));
          if (window.Cart && typeof Cart === "object") Object.assign(Cart, cart);
          return cart;
        })
        .finally(() => this.root?.classList.remove("is-updating"));
    },
    render(cart) {
      if (!this.items) return;
      hydrateInventoryMaxes();
      const items = Array.isArray(cart.items) ? cart.items : [];
      const discountCents = Math.max(0, Number(cart.total_discount || 0), Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
      if (!items.length) {
        this.items.innerHTML = '<div class="amt-cart-drawer__empty">Your cart is empty.</div>';
      } else {
        this.items.innerHTML = items.map((item) => {
          const image = item.featured_image?.url || item.image || "";
          const lineDiscount = Math.max(0, Number(item.total_discount || 0), Number(item.original_line_price || 0) - Number(item.final_line_price || 0));
          const hasLinePriceDrop = Number(item.original_line_price || 0) > Number(item.final_line_price || 0);
          const inventoryMax = getKnownInventoryMax(item.variant_id);
          const plusDisabled = inventoryMax && Number(item.quantity || 0) >= inventoryMax;
          return `
            <article class="amt-cart-drawer__item" data-amt-cart-item data-key="${escapeHTML(item.key)}" data-variant-id="${escapeHTML(item.variant_id)}" data-quantity="${item.quantity}"${inventoryMax ? ` data-inventory-max="${inventoryMax}"` : ""}>
              <a class="amt-cart-drawer__image" href="${escapeHTML(item.url || "#")}">
                ${image ? `<img src="${escapeHTML(image)}" alt="${escapeHTML(item.product_title || item.title || "")}" loading="lazy">` : ""}
              </a>
              <div>
                <div class="amt-cart-drawer__item-top">
                  <a class="amt-cart-drawer__title" href="${escapeHTML(item.url || "#")}">${escapeHTML(item.product_title || item.title || "")}</a>
                  <button class="amt-cart-drawer__remove" type="button" data-amt-cart-remove aria-label="Remove ${escapeHTML(item.product_title || item.title || "item")}">&times;</button>
                </div>
                ${item.variant_title && item.variant_title !== "Default Title" ? `<div class="amt-cart-drawer__variant">${escapeHTML(item.variant_title)}</div>` : ""}
                ${lineDiscount > 0 ? `<div class="amt-cart-drawer__line-discount">Saved ${money(lineDiscount)}</div>` : ""}
                <div class="amt-cart-drawer__item-bottom">
                  <div class="amt-cart-drawer__qty" aria-label="Quantity">
                    <button type="button" data-amt-cart-qty="-1" aria-label="Decrease quantity">-</button>
                    <span>${item.quantity}</span>
                    <button type="button" data-amt-cart-qty="1" aria-label="Increase quantity"${plusDisabled ? " disabled aria-disabled=\"true\"" : ""}>+</button>
                  </div>
                  <div class="amt-cart-drawer__price">
                    ${hasLinePriceDrop ? `<del>${money(item.original_line_price)}</del>` : ""}
                    ${money(item.final_line_price)}
                  </div>
                </div>
              </div>
            </article>
          `;
        }).join("");
      }

      if (this.total) this.total.textContent = money(cart.total_price || 0);
      syncAddToCartButtonStates(cart);
      if (this.discount && this.discountValue) {
        this.discount.hidden = discountCents <= 0;
        this.discountValue.textContent = discountCents > 0 ? `-${money(discountCents)}` : "";
      }
      this.updateShipping(cart);
    },
    updateShipping(cart) {
      if (!this.shipping) return;
      const text = this.shipping.querySelector(".amt-cart-drawer__shipping-text");
      if (text) text.textContent = "Free shipping on all orders";
    }
  };

  const initMenuFallbacks = () => {
    const hydrateMenuTemplates = () => {
      document.querySelectorAll(".header-menu .menu-list > li > template.temp-id, .header-vertical-menu .menu-list > li > template.temp-id").forEach((template) => {
        const item = template.closest("li");
        if (!item || item.querySelector(":scope > .dropdown-menu")) return;
        template.insertAdjacentHTML("beforebegin", template.innerHTML);
      });
    };

    hydrateMenuTemplates();
    document.addEventListener("desktop-lazyloaded", hydrateMenuTemplates);

    document.addEventListener("click", (event) => {
      const wrapper = event.target.closest(".header-menu .menu-list > li .menu-item_wrapper, .header-menu .menu-list > li > a");
      const item = wrapper?.closest("li");
      const dropdown = item?.querySelector(":scope > .dropdown-menu");
      if (!item || !dropdown) return;

      if (!item.classList.contains("amt-menu-open")) {
        event.preventDefault();
        event.stopPropagation();
        document.querySelectorAll(".header-menu .menu-list > li.amt-menu-open").forEach((openItem) => {
          if (openItem !== item) openItem.classList.remove("amt-menu-open");
        });
        item.classList.add("amt-menu-open");
      }
    }, true);

    document.addEventListener("click", (event) => {
      if (!event.target.closest(".header-menu")) {
        document.querySelectorAll(".header-menu .menu-list > li.amt-menu-open").forEach((item) => item.classList.remove("amt-menu-open"));
      }
    });

    document.addEventListener("click", (event) => {
      const head = event.target.closest(".vertical-menu-head");
      const menu = document.querySelector(".header-vertical-menu");
      if (!head || !menu) return;
      event.preventDefault();
      head.classList.toggle("open");
      menu.classList.toggle("open");
      menu.classList.toggle("amt-menu-open");
    }, true);
  };

  const isSearchArtifactText = (value = "") => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return false;
    const compact = trimmed.replace(/\s+/g, "");
    return /[\u00c3\u00c2\u00e2\u00ef\ufffd\u25a1\u2713]/.test(compact)
      || (/[\u00c0-\u00ff\u20ac\u0080-\u009f\u25a1\ufffd]/.test(compact) && compact.length <= 80);
  };

  const cleanSearchArtifacts = () => {
    document.querySelectorAll(".section-search-template, .page-search-template").forEach((section) => {
      section.querySelectorAll(".search-results, .search-results--products, .col-main").forEach((container) => {
        Array.from(container.childNodes).forEach((node) => {
          if (node.nodeType !== Node.TEXT_NODE) return;
          if (isSearchArtifactText(node.nodeValue)) {
            node.remove();
            return;
          }
          const text = node.nodeValue || "";
          const trimmed = text.trim();
          if (/[ÂÃÄÅâœ€�]/.test(text) && (!/[A-Za-z0-9]{2,}/.test(trimmed) || /^[ÂÃÄÅâœ€�\s.,;:!?'"`~_-]+$/.test(trimmed))) {
            node.remove();
          }
        });
      });

      const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT);
      const remove = [];
      while (walker.nextNode()) {
        if (isSearchArtifactText(walker.currentNode.nodeValue)) remove.push(walker.currentNode);
      }
      remove.forEach((node) => node.remove());

      section.querySelectorAll("span, p, strong, em, b, i, div").forEach((element) => {
        const text = element.textContent || "";
        if (text.trim().length > 80) return;
        if (element.querySelector("a, button, input, select, textarea, img, svg, form")) return;
        if (isSearchArtifactText(text)) element.remove();
      });
    });
  };

  const watchSearchArtifacts = () => {
    cleanSearchArtifacts();
  };

  const initSearchFilterShowMore = () => {
    document.addEventListener("click", (event) => {
      const more = event.target.closest(".collection-filters .filter-maxlist .maxlist-more");
      const less = event.target.closest(".collection-filters .filter-maxlist .maxlist-less");
      const control = more || less;
      if (!control) return;

      const list = control.closest(".filter-group_inner, .filter-group_item-wrapper, .filter-group_item-wrapper_scroll");
      if (!list) return;
      event.preventDefault();
      event.stopPropagation();

      const expand = Boolean(more);
      list.querySelectorAll(".filter-group_item.ind-hide").forEach((item) => {
        item.style.display = expand ? "block" : "";
      });
      list.classList.toggle("maxlist-more-active", expand);
      const moreButton = list.querySelector(".filter-maxlist .maxlist-more");
      const lessButton = list.querySelector(".filter-maxlist .maxlist-less");
      if (moreButton) moreButton.classList.toggle("d-none", expand);
      if (lessButton) lessButton.classList.toggle("d-none", !expand);
    }, true);
  };

  const initUnifiedFilters = () => {
    const buildFilterUrl = (form) => {
      const params = new URLSearchParams();
      const current = new URLSearchParams(window.location.search);

      ["q", "type", "options[prefix]", "options[unavailable_products]"].forEach((name) => {
        const field = form.querySelector(`[name="${CSS.escape(name)}"]`);
        const value = field?.value || current.get(name);
        if (value) params.set(name, value);
      });
      if (params.get("q") && !params.get("type")) params.set("type", "product");
      if (params.get("q") && !params.get("options[prefix]")) params.set("options[prefix]", "last");

      form.querySelectorAll("input[type='checkbox']:checked").forEach((input) => {
        if (input.name && input.value) params.append(input.name, input.value);
      });

      form.querySelectorAll("input[type='number']").forEach((input) => {
        if (input.name && input.value !== "") params.set(input.name, input.value);
      });

      const sort = document.querySelector("#select-sort-by");
      if (sort?.value) params.set("sort_by", sort.value);
      return `${window.location.pathname}?${params.toString()}`;
    };

    document.addEventListener("click", (event) => {
      const opener = event.target.closest(".mobile-filter-icon");
      const closer = event.target.closest(".js-sidebar-close, .col-sidebar .btn-close");
      if (opener) {
        document.documentElement.classList.add("overflow-hidden");
        document.body.classList.add("active-sidebar");
      }
      if (closer) {
        document.documentElement.classList.remove("overflow-hidden");
        document.body.classList.remove("active-sidebar");
      }
    }, true);

    document.addEventListener("change", (event) => {
      const form = event.target.closest("#collection-filters-form");
      if (!form || form.dataset.amtFilterNative === "true") return;
      if (!event.target.matches("input[type='checkbox'], #select-sort-by")) return;
      window.setTimeout(() => {
        if (document.querySelector(".collection-template.is-filtering")) return;
        window.location.href = buildFilterUrl(form);
      }, 350);
    });

    document.addEventListener("submit", (event) => {
      const form = event.target.closest("#collection-filters-form");
      if (!form || form.dataset.amtFilterNative === "true") return;
      event.preventDefault();
      window.location.href = buildFilterUrl(form);
    }, true);
  };

  const initConditionFilters = () => {
    const labelForCondition = (value = "") => {
      const normalized = String(value || "").replace(/[-_]+/g, " ").trim();
      return normalized ? normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()) : "";
    };

    const hydrateConditionFilter = (group) => {
      const section = group.closest(".collection-template, .section-search-template") || document;
      const optionContainer = group.querySelector("[data-amt-condition-options]");
      if (!optionContainer) return;

      const cards = Array.from(section.querySelectorAll("product-card[data-amt-condition], [data-amt-search-boost-product][data-amt-condition]"));
      const conditions = new Map();
      cards.forEach((card) => {
        const value = card.dataset.amtCondition || "";
        if (!value) return;
        conditions.set(value, card.dataset.amtConditionLabel || labelForCondition(value));
      });

      if (!conditions.size) {
        group.hidden = true;
        optionContainer.innerHTML = "";
        return;
      }

      group.hidden = false;
      const selected = new Set(Array.from(optionContainer.querySelectorAll("[data-amt-condition-input]:checked")).map((input) => input.value));
      const nextMarkup = Array.from(conditions.entries()).map(([value, label], index) => {
        const id = `amt-condition-${index}-${value}`;
        return `<div class="filter-group_item input-group">
          <input type="checkbox" class="filter filter-group-display__list-item_checkbox" id="${id}" value="${value}" data-amt-condition-input${selected.has(value) ? " checked" : ""}>
          <label for="${id}"><span class="no-js-hidden">${escapeHTML(label)}</span></label>
        </div>`;
      }).join("");
      if (optionContainer.innerHTML.trim() !== nextMarkup.trim()) {
        optionContainer.innerHTML = nextMarkup;
      }
    };

    const applyFallbackFilters = (section) => {
      section = section || document;
      const selectedConditions = Array.from(section.querySelectorAll("[data-amt-condition-input]:checked")).map((input) => input.value);
      const selectedAvailability = Array.from(section.querySelectorAll('[data-amt-fallback-filter="availability"]:checked')).map((input) => input.value);
      const selectedBrands = Array.from(section.querySelectorAll('[data-amt-fallback-filter="brand"]:checked')).map((input) => input.value);
      const minPrice = Number(section.querySelector("[data-amt-fallback-price-min]")?.value || 0) * 100;
      const maxPriceValue = section.querySelector("[data-amt-fallback-price-max]")?.value || "";
      const maxPrice = maxPriceValue === "" ? null : Number(maxPriceValue) * 100;
      const cards = Array.from(section.querySelectorAll("product-card, [data-amt-search-boost-product]"));
      cards.forEach((card) => {
        const price = Number(card.dataset.amtPrice || 0);
        const matchesCondition = !selectedConditions.length || selectedConditions.includes(card.dataset.amtCondition || "");
        const matchesAvailability = !selectedAvailability.length || selectedAvailability.includes(card.dataset.amtAvailability || "");
        const matchesBrand = !selectedBrands.length || selectedBrands.includes(card.dataset.amtBrand || "");
        const matchesMinPrice = !minPrice || !price || price >= minPrice;
        const matchesMaxPrice = maxPrice === null || !price || price <= maxPrice;
        const matches = matchesCondition && matchesAvailability && matchesBrand && matchesMinPrice && matchesMaxPrice;
        card.classList.toggle("d-none", !matches);
        card.toggleAttribute("hidden", !matches);
      });
    };

    const hydrateAll = () => {
      const sections = new Set();
      document.querySelectorAll("[data-amt-condition-filter]").forEach((group) => {
        hydrateConditionFilter(group);
        sections.add(group.closest(".collection-template, .section-search-template") || document);
      });
      document.querySelectorAll("[data-amt-fallback-filter], [data-amt-fallback-price-min], [data-amt-fallback-price-max]").forEach((control) => {
        sections.add(control.closest(".collection-template, .section-search-template") || document);
      });
      sections.forEach((section) => {
        applyFallbackFilters(section);
      });
    };

    hydrateAll();
    document.addEventListener("change", (event) => {
      const input = event.target.closest("[data-amt-condition-input], [data-amt-fallback-filter]");
      if (!input) return;
      applyFallbackFilters(input.closest(".collection-template, .section-search-template") || document);
    });
    document.addEventListener("click", (event) => {
      const button = event.target.closest("[data-amt-fallback-price-apply]");
      if (!button) return;
      event.preventDefault();
      applyFallbackFilters(button.closest(".collection-template, .section-search-template") || document);
    });

    document.addEventListener("amt:products-rendered", hydrateAll);
  };

  const initCartPageDiscounts = () => {
    if (!document.body.classList.contains("page-cart")) return;
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        const totalDiscount = Math.max(0, Number(cart.total_discount || 0), Number(cart.original_total_price || 0) - Number(cart.total_price || 0));
        if (totalDiscount <= 0) return;
        document.querySelectorAll("[data-amt-cart-page-discount]").forEach((row) => {
          row.hidden = false;
          const value = row.querySelector("[data-amt-cart-page-discount-value]");
          if (value) value.textContent = `-${money(totalDiscount)}`;
        });
      })
      .catch(() => {});
  };

  const initCartIconDrawer = () => {
    const selector = ".header-cart a[href*='/cart']:not(.amt-cart-drawer__view), .js-header-cart-icon--desktop, .js-header-cart-icon--mobile, a[href='/cart']:not(.amt-cart-drawer__view)";

    const bindTrigger = (trigger) => {
      if (!trigger || trigger.dataset.amtCartDrawerBound === "true") return;
      trigger.dataset.amtCartDrawerBound = "true";

      const openDrawer = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
        document.querySelectorAll(".header-cart.active").forEach((cart) => cart.classList.remove("active"));
        drawer.open();
      };

      trigger.addEventListener("pointerdown", openDrawer, { capture: true });
      trigger.addEventListener("click", openDrawer, { capture: true });
    };

    const bindIn = (root = document) => {
      if (root.nodeType !== 1 && root !== document) return;
      if (root.matches?.(selector)) bindTrigger(root);
      root.querySelectorAll?.(selector).forEach(bindTrigger);
    };

    bindIn(document);
    document.addEventListener("desktop-lazyloaded", () => bindIn(document), { once: true });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach(bindIn);
      });
    });
    observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);

    document.addEventListener("click", (event) => {
      const link = event.target.closest(selector);
      if (!link || link.closest("[data-amt-cart-drawer]") || link.classList.contains("amt-cart-drawer__view")) return;
      event.preventDefault();
      event.stopPropagation();
      document.querySelectorAll(".header-cart.active").forEach((cart) => cart.classList.remove("active"));
      drawer.open();
    }, true);
  };

  const initCheckoutInventoryGuard = () => {
    const getOverLimitCartLines = () => {
      hydrateInventoryMaxes();
      return Array.from(document.querySelectorAll("[data-amt-cart-item], .cart-wrapper cart-line-item, .cart-wrapper .js-cart-line-item")).map((lineItem) => {
        const max = toPositiveInt(lineItem.dataset.inventoryMax);
        const quantity = parseInt(lineItem.dataset.quantity || lineItem.querySelector?.("input[type='number']")?.value || 0, 10) || 0;
        const key = lineItem.dataset.key;
        return { lineItem, key, max, quantity };
      }).filter((line) => line.key && line.max && line.quantity > line.max);
    };

    document.addEventListener("click", (event) => {
      const checkout = event.target.closest("a[href*='/checkout'], button[name='checkout'], input[name='checkout'], .btn-cart-checkout, .cart-mobile-checkout, .amt-cart-drawer__checkout");
      if (!checkout) return;
      const overLimitLines = getOverLimitCartLines();
      if (!overLimitLines.length) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();

      showInventoryWarning(checkout, "Cart quantity was higher than available stock. I corrected it before checkout.");
      Promise.all(overLimitLines.map((line) => fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: line.key, quantity: line.max })
      }).then((response) => response.json()).catch(() => null)))
        .then(() => drawer.refresh())
        .catch(() => {});
    }, true);
  };

  const initAddToCartDrawer = () => {
    drawer.init();
    const submitAddForm = (form, trigger) => {
      if (!form || form.dataset.amtAdding === "true") return;
      const formData = new FormData(form);
      if (!formData.get("quantity")) formData.set("quantity", "1");
      if (!formData.get("id")) return;
      if (isQuoteOnlyForm(form)) {
        showInventoryWarning(form, "Please request a quote for this product.");
        return;
      }

      form.dataset.amtAdding = "true";
      trigger?.setAttribute("aria-busy", "true");

      validateAddToCartInventory(form, formData)
        .then((canAdd) => {
          if (!canAdd) return null;
          return fetch("/cart/add.js", {
            method: "POST",
            headers: { Accept: "application/json" },
            body: formData
          });
        })
        .then((response) => {
          if (!response) return null;
          if (!response.ok) return response.json().then((error) => Promise.reject(error));
          return response.json();
        })
        .then((lineItem) => {
          if (lineItem) drawer.open();
        })
        .catch((error) => {
          showInventoryWarning(form, error?.description || error?.message || inventoryWarningText);
          form.dataset.amtAdding = "false";
        })
        .finally(() => {
          form.dataset.amtAdding = "false";
          trigger?.removeAttribute("aria-busy");
        });
    };

    document.addEventListener("submit", (event) => {
      const form = event.target.closest?.("form[action*='/cart/add']");
      if (!form || form.dataset.amtNativeSubmit === "true") return;
      event.preventDefault();
      event.stopPropagation();
      submitAddForm(form, form.querySelector("[type='submit'], button[name='add'], .btn-add-to-cart"));
    }, true);

    document.addEventListener("click", (event) => {
      if (event.target.closest("[data-rfq-open]")) return;
      const buyNow = event.target.closest(".js-buy-now-btn");
      const buyNowForm = buyNow?.closest("form[action*='/cart/add']") || buyNow?.form;
      if (buyNow && buyNowForm?.matches("form[action*='/cart/add']")) {
        event.preventDefault();
        event.stopPropagation();
        const formData = new FormData(buyNowForm);
        if (!formData.get("quantity")) formData.set("quantity", "1");
        validateAddToCartInventory(buyNowForm, formData)
          .then((canAdd) => {
            if (!canAdd) return null;
            buyNow.setAttribute("aria-busy", "true");
            return fetch("/cart/add.js", {
              method: "POST",
              headers: { Accept: "application/json" },
              body: formData
            });
          })
          .then((response) => {
            if (!response) return null;
            if (!response.ok) return response.json().then((error) => Promise.reject(error));
            window.location.href = "/checkout";
            return null;
          })
          .catch((error) => showInventoryWarning(buyNowForm, error?.description || error?.message || inventoryWarningText))
          .finally(() => buyNow.removeAttribute("aria-busy"));
        return;
      }

      const trigger = event.target.closest(".js-atc-btn, .btn-add-to-cart, button[name='add']");
      const form = trigger?.closest("form[action*='/cart/add']") || trigger?.form;
      if (!trigger || !form || !form.matches("form[action*='/cart/add']")) return;
      event.preventDefault();
      event.stopPropagation();
      submitAddForm(form, trigger);
    }, true);

    document.addEventListener("cart-add", () => {
      window.setTimeout(() => drawer.open(), 80);
    });
    document.addEventListener("change", (event) => {
      const field = event.target.closest("form[action*='/cart/add'] [name='id']");
      if (field) updateProductFormVariantMax(field.closest("form"));
    }, true);
    document.addEventListener("click", (event) => {
      const plusButton = event.target.closest("form[action*='/cart/add'] quantity-input-plus");
      if (!plusButton) return;
      const form = plusButton.closest("form[action*='/cart/add']");
      updateProductFormVariantMax(form);
      const quantityField = form?.querySelector("[name='quantity']");
      const max = toPositiveInt(quantityField?.dataset.inventoryMax || quantityField?.getAttribute("max"));
      const current = parseInt(quantityField?.value || 1, 10) || 1;
      if (max && current >= max) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
        quantityField.value = max;
        showInventoryWarning(form, `Only ${max} item${max === 1 ? "" : "s"} available for this product.`);
      }
    }, true);
    document.addEventListener("cart-change", (event) => {
      hydrateInventoryMaxes();
      syncAddToCartButtonStates(event.detail);
    });
    hydrateInventoryMaxes();
    document.querySelectorAll("form[action*='/cart/add']").forEach(updateProductFormVariantMax);
    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then(syncAddToCartButtonStates)
      .catch(() => {});
    window.AMTCartDrawer = drawer;
  };

  const initRfqTextNormalizer = () => {
    const normalize = (root = document) => {
      root.querySelectorAll?.("[data-rfq-open], .btn-rfq-card, .btn-rfq-pdp, .rfq-collection-btn, [class*='rfq-collection-btn']").forEach((button) => {
        button.querySelectorAll("span, .text").forEach((textNode) => {
          if (/add\s*to\s*quote/i.test(textNode.textContent || "")) textNode.textContent = "Request Quote";
        });
        if (/add\s*to\s*quote/i.test(button.textContent || "") && !button.querySelector("span, .text")) {
          button.textContent = "Request Quote";
        }
      });
    };

    normalize();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") {
          normalize(document);
          return;
        }
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) normalize(node);
        });
      });
    });
    observer.observe(document.body || document.documentElement, { childList: true, characterData: true, subtree: true });
    let attempts = 0;
    const interval = window.setInterval(() => {
      normalize();
      attempts += 1;
      if (attempts >= 20) window.clearInterval(interval);
    }, 500);
  };

  const init = () => {
    const isListingPage = document.body.classList.contains("template-collection")
      || document.body.classList.contains("template-search")
      || document.querySelector(".section-search-template, .page-collection-template, #collection-filters-form");

    initAddToCartDrawer();
    initRfqTextNormalizer();
    initMenuFallbacks();
    if (isListingPage) {
      watchSearchArtifacts();
      initSearchFilterShowMore();
      initUnifiedFilters();
      initConditionFilters();
    }
    initCartPageDiscounts();
    initCartIconDrawer();
    initCheckoutInventoryGuard();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
